import React from 'react';
import I18n from 'i18n-js';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import BestSellerBadge from './best_seller_badge';
import { findProductFamilySpec } from '@reverbdotcom/commons/src/product_family_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { CSP, Category } from '../schemas/rql';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';

interface IProps {
  csp: CSP;
  categories: Category[];
  componentName: string;
}

function getLowestPrice(inventory) {
  if (!inventory.newTotal && !inventory.usedTotal) return null;
  if (!inventory.newTotal) return inventory.usedLowPrice?.display;
  if (!inventory.usedTotal) return inventory.newLowPrice?.display;

  return inventory.usedLowPrice?.amountCents < inventory.newLowPrice?.amountCents ?
    inventory.usedLowPrice?.display : inventory.newLowPrice?.display;
}

export function componentClickEvent(displayStyle: string, path: string, componentName: string, user: Partial<IUser>) {
  return {
    eventName: MParticleEventName.ClickedComponent,
    componentName: componentName,
    displayStyle,
    entityType: 'url',
    entityId: path,
    experiments: formatExperimentsForEventAttributes(
      user,
      [
        experiments.CSP_CTA_ON_ITEM_SIDEBAR,
      ],
    ),
  };
}

export function Item2ProductModuleHeader(props: IProps) {
  const { csp, categories, componentName } = props;
  const inCspCtaSidebarExperiment = useExpEnabled(experiments.CSP_CTA_ON_ITEM_SIDEBAR);
  const user = useUser();
  if (!csp) return null;

  const lowestPrice = getLowestPrice(csp.inventory);
  const totalInventoryCount = csp.inventory?.usedTotal + csp.inventory?.newTotal;
  const productFamily = findProductFamilySpec(csp.specs?.productSpecs);

  return (
    <>
      <div className="item2-product-module__header">
        {csp.image?.source &&
          <CoreLink
            to={csp.webLink?.path}
            className="item2-product-module__image-link"
            clickEvent={componentClickEvent('imageLink', csp.webLink?.path, componentName, user)}
          >
            <div className="item2-product-module__image">
              <img src={csp.image.source} alt="" />
            </div>
          </CoreLink>
        }
        <div className="item2-product-module__content">
          { inCspCtaSidebarExperiment ?
            (<CoreLink
              to={csp.webLink?.path}
              className="item2-product-module__title"
              clickEvent={componentClickEvent('titleLink', csp.webLink?.path, componentName, user)}
            >
              {csp.title}
            </CoreLink>) :
            (<h4>
              <CoreLink
                to={csp.webLink?.path}
                className="item2-product-module__title"
                clickEvent={componentClickEvent('titleLink', csp.webLink?.path, componentName, user)}
              >
                {csp.title}
              </CoreLink>
            </h4>)
          }
          <div className="item2-product-module__badges">
            <BestSellerBadge
              cspId={csp.id}
              categories={categories}
            />
          </div>
        </div>
      </div>
      <div className="item2-product-module__actions">
        <div className="item2-product-module__action">
          {totalInventoryCount > 1 &&
            <CoreLink
              to={csp.webLink?.path}
              className="item2-product-module__action__button"
              clickEvent={componentClickEvent('ctaLink', csp.webLink?.path, componentName, user)}
            >
              {I18n.t('discovery.item2ProductModule.lowestFrom', { count: totalInventoryCount, price: lowestPrice })}
            </CoreLink>
          }
        </div>
        {!inCspCtaSidebarExperiment &&
          <div className="item2-product-module__action">
            {productFamily?.path &&
              <CoreLink
                to={productFamily.path}
                className="item2-product-module__action__button"
                clickEvent={componentClickEvent('productFamilyLink', productFamily.path, componentName, user)}
              >
                {I18n.t('discovery.item2ProductModule.productFamily', { name: productFamily.value })}
              </CoreLink>
            }
          </div>
        }
      </div>
    </>
  );
}
